// router/guards.js
import * as Sentry from '@sentry/browser';

// 스크롤 동작은 그대로 사용합니다.
export const scrollBehavior = (to, from, savedPosition) => {
    if (savedPosition) return savedPosition;
    let y = 0;
    // 특정 라우트에서 스크롤 위치 조정
    if (to.name === 'CompanyAnswers' && to.query.question_code) {
        y = 200;
    }
    return { x: 0, y };
};

/**
 * Vue Router의 네비게이션 가드를 설정합니다.
 * 이 함수는 라우터 인스턴스를 받아 필요한 가드를 모두 등록합니다.
 * @param {object} router - Vue Router 인스턴스
 */
export function setupNavigationGuards(router) {
    // 마지막 네비게이션 정보를 저장하여 중복 호출을 방지합니다.
    let lastNavigation = {
        key: null,
        time: 0,
    };
    // chunk 로딩 실패 시 다시 열 목적지 (실패한 네비게이션은 URL 이 바뀌지 않는다)
    let targetPath = null;
    
    // 전역 beforeEach 가드
    router.beforeEach((to, from, next) => {
        const currentTime = Date.now();
        // location 객체를 고유한 문자열 키로 만듭니다.
        const navigationKey = `${to.path}?${JSON.stringify(to.query)}#${to.hash}`;
        
        // 100ms 이내에 동일한 경로로의 네비게이션 요청은 무시 (사용자 더블클릭 방지)
        if (
            lastNavigation.key === navigationKey &&
            currentTime - lastNavigation.time < 100
        ) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🛡️ Rapid navigation prevented:', navigationKey);
            }
            return next(false); // 네비게이션 취소
        }
        
        // 마지막 네비게이션 정보 업데이트
        lastNavigation.key = navigationKey;
        lastNavigation.time = currentTime;
        targetPath = to.fullPath;
        
        // 정상적인 경우 다음으로 진행
        next();
    });
    
    // 전역 afterEach 가드 (주로 로깅이나 분석에 사용)
    router.afterEach((to, from) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Navigation successful: ${from.fullPath} -> ${to.fullPath}`);
        }
    });
    
    // 전역 onError 핸들러 (네비게이션 에러 중앙 처리)
    router.onError(error => {
        // 무시해도 되는 일반적인 네비게이션 에러 목록
        const ignorableErrors = [
            'NavigationDuplicated',
            'NavigationCancelled',
            'NavigationAborted',
        ];
        
        // 무시할 수 있는 에러인 경우, 개발 환경에서만 경고를 표시하고 실제 에러는 던지지 않음
        if (ignorableErrors.includes(error.name)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`⚠️ Router navigation warning (ignored): ${error.message}`);
            }
        } else if (/ChunkLoadError|Loading (CSS )?chunk .* failed|Failed to fetch dynamically imported module/i.test(`${error.name} ${error.message}`)) {
            // 새 배포로 옛 chunk 가 사라진 경우: 최신 번들을 받도록 목적지를 다시 열어 준다.
            // 30초 안에 또 실패하면(진짜 장애) 무한 새로고침 대신 보고만 한다.
            let lastReload = 0;
            try { lastReload = Number(sessionStorage.getItem('chunk_reload_at')) || 0; } catch (e) { /* ignore */ }
            if (targetPath && Date.now() - lastReload > 30000) {
                try { sessionStorage.setItem('chunk_reload_at', Date.now()); } catch (e) { /* ignore */ }
                window.location.assign(targetPath);
            } else {
                Sentry.captureException(error);
            }
        } else {
            // 그 외의 중요한 에러는 콘솔에 에러로 표시
            if (process.env.NODE_ENV === 'development') {
                console.error('🚨 Unhandled router error:', error);
            }
            Sentry.captureException(error);
        }
    });
}
